/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2021 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * React component for Device Management Popup
 *
 * @file AccessGranted.jsx
 * @author Infant Diana Sebastian
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  IconButton,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Framework from '@greenville/framework';
import { Icon } from '@greenville/mui-theme';
import CommonUtils from '../utils/CommonUtils';
import * as constants from '../constants';
import env from '../env';

const DialogPaper = styled('div')({
  '&&': {
    background: '#F7F9FD',
    paddingLeft: 20,
    boxShadow: '0px 2px 8px rgb(7 15 31 / 10%), 0px 4px 24px rgb(7 15 31 / 10%)',
    borderRadius: 8,
    outline: 'none'
  }
});

const DialogTitleStyled = styled(DialogTitle)({
  '&&': {
    fontFamily: 'TT Commons',
    fontSize: 28,
    lineHeight: '110%',
    color: ' #05112A',
    fontWeight: 'bold',
    width: '50%',
    paddingBottom: 0,
    paddingTop: 0,
    marginTop: '-22px'
  }
});

const DialogContentStyled = styled(DialogContent)({
  '&&': {
    fontFamily: 'Hind',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: 16,
    color: '#333333',
    paddingTop: 8,
    paddingBottom: 8
  }
});

const DialogActionsStyled = styled(DialogActions)({
  '&&': {
    justifyContent: 'flex-start',
    marginTop: 0,
    paddingLeft: 0,
    marginBottom: 64,
    paddingRight: 8,
    paddingBottom: 8,
    marginRight: 24,
    marginLeft: 24,
    paddingTop: 0
  }
});

const RemoveActions = styled(DialogActions)({
  '&&': {
    marginLeft: '24px',
    marginBottom: '24px',
    padding: '8px',
    marginTop: 7,
    marginRight: 7
  }
});

const PasswordChangeButton = styled(Button)({
  '&&': {
    border: 'none',
    background: 'linear-gradient(135deg, #EE512E 0%, #DF006B 63.44%, #BD0071 100%)',
    '&:hover': {
      background: 'linear-gradient(135deg, #D6492A 0%, #BA015A 63.44%, #940159 100%)'
    }
  }
});

const PasswordChangeText = styled(Typography)({
  '&&': {
    fontFamily: 'Hind',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: 16,
    lineHeight: '150%',
    color: '#FEFEFE'
  }
});

const AccessGranted = ({ open, onClose }) => {
  const fullScreen = useMediaQuery(useTheme().breakpoints.down('sm'));
  const language = Framework.getStoreRegistry().getStore('language');

  const validateURL = (url) => {
    const parsed = new URL(url);

    return ['https:', 'http:'].includes(parsed.protocol);
  };
  const handleChangePassword = () => {
    CommonUtils.deviceManagementOTPEvents(constants.LINK_SUCCESS_MODAL, constants.MFA_SUCCESS_MODAL, constants.CLICK_LINK, constants.CHANGE_PASSWORD);
    if (validateURL(env.BOOKSHELF_CONFIG.changePassword)) {
      window.open(env.BOOKSHELF_CONFIG.changePassword, '_self');
    }
  };

  return (
    <Dialog
      open={open}
      id="access-granted-dialog"
      fullScreen={fullScreen}
      onClose={onClose}
      maxWidth="sm"
      slotProps={{
        paper: {
          component: DialogPaper
        }
      }}
      aria-labelledby="access-granted-dialog-title"
      aria-describedby="access-granted-dialog-description"
      role="dialog"
      aria-modal="true"
      className="dialog-zindex"
    >
      <RemoveActions>
        <IconButton
          id="remove-button"
          onClick={onClose}
          aria-label={language.getMessage('common.CLOSE')}
        >
          <Icon id="remove-icon" name="Remove" fontSize="small" />
        </IconButton>
      </RemoveActions>
      <DialogTitleStyled
        disableTypography
        id="access-granted-dialog-title"
      >
        <FormattedMessage {...language.getText('device.ACCESS_HEADER')} />
      </DialogTitleStyled>
      <DialogContentStyled id="access-granted-dialog-description">
        <FormattedMessage
          {...language.getText('device.ACCESS_DESC')}
        />
      </DialogContentStyled>
      <DialogActionsStyled>
        <PasswordChangeButton
          id="change-password"
          color="default"
          onClick={handleChangePassword}
          size="medium"
          variant="outlined"
        >
          <PasswordChangeText>
            <FormattedMessage
              {...language.getText('device.ACCESS_CHANGE_PASSWORD')}
            />
          </PasswordChangeText>
        </PasswordChangeButton>
      </DialogActionsStyled>
    </Dialog>
  );
};

AccessGranted.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default AccessGranted;
